import { useState, useEffect } from 'react';
import { Loader2, Rss, ExternalLink, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};


export default function PublicationOverviewTab({ pubId, publication }) {
  const [subs, setSubs]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/feeds/publications/${pubId}/subscriptions`);
        setSubs(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [pubId]);

  const activeSubs = subs.filter(s => s.is_active);
  const healthyCount = activeSubs.filter(s => s.health_status === 'healthy').length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h2 className="text-sm uppercase tracking-wider text-slate-400 font-semibold mb-4">Publication info</h2>
          <dl className="space-y-3 text-sm">
            <DetailRow label="Name" value={publication.name} />
            <DetailRow label="Domain" value={publication.domain || '—'} />
            <DetailRow label="Site type" value={publication.site_type || '—'} />
            <DetailRow label="Active subscriptions" value={`${publication.active_subscriptions || 0} / ${publication.total_subscriptions || 0}`} />
            <DetailRow label="Stories published" value={publication.stories_published || 0} />
            <DetailRow label="Last fetch" value={fmtUK(publication.latest_fetch_at)} />
          </dl>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm uppercase tracking-wider text-slate-400 font-semibold">Source health</h2>
            {activeSubs.length > 0 && (
              <span className={`text-xs font-semibold ${
                healthyCount === activeSubs.length ? 'text-emerald-600' :
                healthyCount > 0                   ? 'text-amber-600' :
                                                     'text-red-600'
              }`}>
                {healthyCount} / {activeSubs.length} healthy
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6 gap-2 text-slate-400 text-sm">
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          ) : subs.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">
              No subscriptions yet.
            </p>
          ) : (
            <div className="space-y-1.5">
              {subs.slice(0, 6).map(s => {
                const colour =
                  s.health_status === 'healthy'  ? 'bg-emerald-500' :
                  s.health_status === 'warning'  ? 'bg-amber-500' :
                  s.health_status === 'failed'   ? 'bg-red-500' :
                                                   'bg-slate-300';
                return (
                  <Link key={s.id} to={`/feeds/sources/${s.feed_source_id}`}
                    className="flex items-center gap-2 py-1 hover:bg-slate-50 rounded px-2 -mx-2 transition-colors">
                    <span className={`w-2 h-2 rounded-full ${colour} shrink-0`} />
                    <span className="text-sm text-slate-700 truncate flex-1">{s.source_name}</span>
                    {!s.is_active && (
                      <span className="text-[9px] uppercase text-slate-400">paused</span>
                    )}
                    <ExternalLink size={11} className="text-slate-400" />
                  </Link>
                );
              })}
              {subs.length > 6 && (
                <p className="text-[11px] text-slate-400 pt-2 text-center">
                  + {subs.length - 6} more in Subscriptions tab
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function DetailRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs text-slate-400 uppercase tracking-wider font-semibold shrink-0 pt-0.5">{label}</dt>
      <dd className="text-sm text-slate-900 text-right">{value}</dd>
    </div>
  );
}